import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Protected } from "@/lib/Protected";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import MockTests from "@/pages/MockTests";
import MockExams from "@/pages/MockExams";
import MockExamRunner from "@/pages/MockExamRunner";
import TestPlayer from "@/pages/TestPlayer";
import WritingEvaluator from "@/pages/WritingEvaluator";
import SpeakingExaminer from "@/pages/SpeakingExaminer";
import SpeakingRoom from "@/pages/SpeakingRoom";
import ExamHub from "@/pages/ExamHub";
import ExamPlayer from "@/pages/ExamPlayer";
import ExamChoice from "@/pages/ExamChoice";
import MockRunner from "@/pages/MockRunner";
import IELTSStart from "@/pages/IELTSStart";
import IELTSGuidelinesHub, { IELTSGuidelinesWizard } from "@/pages/IELTSGuidelines";
import IELTSListeningMock from "@/pages/IELTSListeningMock";
import IELTSMockHub from "@/pages/IELTSMockHub";
import IELTSPaperListening from "@/pages/IELTSPaperListening";
import IELTSReadingMock from "@/pages/IELTSReadingMock";
import IELTSWritingMock from "@/pages/IELTSWritingMock";
import DesignPreview from "@/pages/DesignPreview";
import LandingPreview from "@/pages/LandingPreview";
import LoginPreview from "@/pages/LoginPreview";
import SignupPreview from "@/pages/SignupPreview";
import FreeAssessmentLead from "@/pages/FreeAssessmentLead";
import FreeAssessmentSpeaking from "@/pages/FreeAssessmentSpeaking";
import FreeAssessmentResult from "@/pages/FreeAssessmentResult";
import FreeAssessmentTerminal from "@/pages/FreeAssessmentTerminal";
import PricingPreview from "@/pages/PricingPreview";

// Small wrappers: inject examId from route into useParams output so the page sees it.
const ExamHubWrap = ({ examId }) => <ExamHub examIdOverride={examId} />;
const ExamPlayerWrap = ({ examId }) => <ExamPlayer examIdOverride={examId} />;
const MockRunnerWrap = ({ examId }) => <MockRunner examIdOverride={examId} />;

import AICoachPage from "@/pages/AICoachPage";
import Classrooms from "@/pages/Classrooms";
import ClassroomRoom from "@/pages/ClassroomRoom";
import TrialBooking from "@/pages/TrialBooking";
import Pricing from "@/pages/Pricing";
import BillingResult from "@/pages/BillingResult";
import Profile from "@/pages/Profile";
import NotesAndAssignments from "@/pages/NotesAndAssignments";
import TeacherDashboard from "@/pages/TeacherDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminContent from "@/pages/AdminContent";
import AdminLeads from "@/pages/AdminLeads";
import AdminPayments from "@/pages/AdminPayments";
import ListeningCatalogue from "@/pages/ListeningCatalogue";
import ListeningPlayer from "@/pages/ListeningPlayer";
import AdminListeningCatalogue from "@/pages/AdminListeningCatalogue";
import AdminUsers from "@/pages/AdminUsers";
import FinanceDashboard from "@/pages/FinanceDashboard";
import Leaderboard from "@/pages/Leaderboard";
import SiteFooter from "@/components/lingo/SiteFooter";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/pricing" element={<PricingPreview />} />
          <Route path="/free-assessment" element={<FreeAssessmentTerminal />} />
          <Route path="/app/assessment/start" element={<Navigate to="/free-assessment" replace />} />
          <Route path="/free-assessment/legacy" element={<FreeAssessmentLead />} />
          <Route path="/free-assessment/speaking" element={<FreeAssessmentSpeaking />} />
          <Route path="/free-assessment/result" element={<FreeAssessmentResult />} />
          <Route path="/trial" element={<TrialBooking />} />
          <Route path="/billing/success" element={<BillingResult result="success" />} />
          <Route path="/billing/cancel" element={<BillingResult result="cancel" />} />
          <Route path="/design-preview" element={<DesignPreview />} />
          <Route path="/design-preview/landing" element={<LandingPreview />} />
          <Route path="/design-preview/login" element={<LoginPreview />} />
          <Route path="/design-preview/signup" element={<SignupPreview />} />
          <Route path="/design-preview/free-assessment" element={<FreeAssessmentLead />} />
          <Route path="/design-preview/free-assessment/speaking" element={<FreeAssessmentSpeaking />} />
          <Route path="/design-preview/free-assessment/result" element={<FreeAssessmentResult />} />
          <Route path="/design-preview/pricing" element={<PricingPreview />} />

          <Route path="/onboarding" element={<Protected redirectOnboarding={false}><Onboarding /></Protected>} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/app/exam/:examId" element={<Protected><ExamChoice /></Protected>} />
          <Route path="/app/tests" element={<Protected><MockTests /></Protected>} />
          <Route path="/app/mock" element={<Protected><MockExams /></Protected>} />
          <Route path="/app/mock/:id" element={<Protected><MockExamRunner /></Protected>} />
          <Route path="/app/strategy" element={<Protected><MockTests /></Protected>} />
          <Route path="/app/tests/:id" element={<Protected><TestPlayer /></Protected>} />
          <Route path="/app/writing" element={<Protected><WritingEvaluator /></Protected>} />
          <Route path="/app/speaking" element={<Protected><SpeakingExaminer /></Protected>} />
          <Route path="/app/speaking-room" element={<Protected><SpeakingRoom /></Protected>} />
          <Route path="/app/ielts/start" element={<Protected><IELTSStart /></Protected>} />
          <Route path="/app/ielts/guidelines" element={<Protected><IELTSGuidelinesHub /></Protected>} />
          <Route path="/app/ielts/guidelines/:module" element={<Protected><IELTSGuidelinesWizard /></Protected>} />
          <Route path="/app/ielts/mock" element={<Protected><IELTSMockHub /></Protected>} />
          <Route path="/app/ielts/listening-mock" element={<Protected><IELTSListeningMock /></Protected>} />
          <Route path="/app/ielts/listening-paper" element={<Protected><IELTSPaperListening /></Protected>} />
          <Route path="/app/ielts/reading-mock" element={<Protected><IELTSReadingMock /></Protected>} />
          <Route path="/app/ielts/writing-mock" element={<Protected><IELTSWritingMock /></Protected>} />
          <Route path="/app/pte" element={<Protected><ExamHubWrap examId="pte" /></Protected>} />
          <Route path="/app/duolingo" element={<Protected><ExamHubWrap examId="duolingo" /></Protected>} />
          <Route path="/app/oxford" element={<Protected><ExamHubWrap examId="oxford" /></Protected>} />
          <Route path="/app/pte/mock" element={<Protected><MockRunnerWrap examId="pte" /></Protected>} />
          <Route path="/app/duolingo/mock" element={<Protected><MockRunnerWrap examId="duolingo" /></Protected>} />
          <Route path="/app/oxford/mock" element={<Protected><MockRunnerWrap examId="oxford" /></Protected>} />
          <Route path="/app/pte/:typeId" element={<Protected><ExamPlayerWrap examId="pte" /></Protected>} />
          <Route path="/app/duolingo/:typeId" element={<Protected><ExamPlayerWrap examId="duolingo" /></Protected>} />
          <Route path="/app/oxford/:typeId" element={<Protected><ExamPlayerWrap examId="oxford" /></Protected>} />
          <Route path="/app/coach" element={<Protected><AICoachPage /></Protected>} />
          <Route path="/app/classrooms" element={<Protected><Classrooms /></Protected>} />
          <Route path="/app/classrooms/:id" element={<Protected><ClassroomRoom /></Protected>} />
          <Route path="/app/library" element={<Protected><NotesAndAssignments /></Protected>} />
          <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/app/leaderboard" element={<Protected><Leaderboard /></Protected>} />
          <Route path="/app/teacher" element={<Protected><TeacherDashboard /></Protected>} />
          <Route path="/app/admin" element={<Protected><AdminDashboard /></Protected>} />
          <Route path="/app/admin/content" element={<Protected><AdminContent /></Protected>} />
          <Route path="/app/admin/leads" element={<Protected><AdminLeads /></Protected>} />
          <Route path="/app/admin/payments" element={<Protected><AdminPayments /></Protected>} />
          <Route path="/app/admin/listening-catalogue" element={<Protected><AdminListeningCatalogue /></Protected>} />
          <Route path="/app/listening-catalogue" element={<Protected><ListeningCatalogue /></Protected>} />
          <Route path="/app/listening-catalogue/:id" element={<Protected><ListeningPlayer /></Protected>} />
          <Route path="/app/admin/users" element={<Protected><AdminUsers /></Protected>} />
          <Route path="/app/finance" element={<Protected><FinanceDashboard /></Protected>} />
        </Routes>
        <SiteFooter variant="fixed" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
